import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SuccessModal } from "./successModal";
import { useState, useContext } from "react";
import {
  NewsletterContext,
  NewsletterConfig,
} from "../context/NewsletterContext";
import { toneOptions } from "../config/toneOptions";
import { Loader2 } from "lucide-react";
import { useGenerateNLwTone } from "../hooks/useGenerateNLwTone";
import { useNewsletterConfig } from "../hooks/useNewsletterConfig";

export const SetTone: React.FC = () => {
  const {
    config: { tone, toneFeedback },
    preview,
    updateTone,
  } = useContext(NewsletterContext);
  const { generateNewsletter, isGenerating } = useGenerateNLwTone();

  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  const handleToneClick = async (toneOption: string, toneFeedback: string) => {
    updateTone(toneOption as NewsletterConfig["tone"], toneFeedback);
    if (toneOption === "Make my own") {
      setShowFeedbackForm(true);
    } else {
      setShowFeedbackForm(false);
      await generateNewsletter(toneOption, toneFeedback);
    }
  };

  return (
    <div
      className="mb-12 border border-black rounded-xl overflow-hidden shadow-md"
      style={{
        background:
          "linear-gradient(135deg, #fce7f3 0%, #fbcfe8 50%, #fecdd3 100%)",
      }}
    >
      <div
        className="p-6 border-b border-black"
        style={{
          background: "linear-gradient(135deg, #fbcfe8 0%, #fed7aa 100%)",
        }}
      >
        <h2 className="text-lg font-medium">
          What's the vibe of this newsletter?
        </h2>
      </div>
      <div className="p-6 space-y-6">
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {toneOptions.map((option) => (
              <button
                key={option.tone}
                onClick={() =>
                  handleToneClick(option.tone, option.toneFeedback)
                }
                disabled={isGenerating}
                className={`w-full py-4 px-6 font-medium text-base transition-all rounded-lg border border-black ${
                  tone === option.tone && !isGenerating
                    ? "bg-black text-white rotate-2 transform"
                    : isGenerating && tone === option.tone
                    ? "bg-gray-100 opacity-60 cursor-not-allowed relative"
                    : "bg-white hover:bg-gray-50"
                }`}
              >
                <div className="text-lg mb-2 flex items-center gap-2">
                  {isGenerating && tone === option.tone ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <span className="text-2xl">{option.icon}</span>
                  )}
                  {option.tone.charAt(0).toUpperCase() + option.tone.slice(1)}
                </div>
                <div className="text-sm font-normal">{option.description}</div>
              </button>
            ))}
          </div>
        </div>
        {showFeedbackForm && (
          <ToneFeedbackForm
            generateNewsletter={generateNewsletter}
            updateTone={(tone, toneFeedback) =>
              updateTone(tone as NewsletterConfig["tone"], toneFeedback)
            }
            initialFeedback={toneFeedback}
          />
        )}
        {isGenerating ? (
          <Card className="bg-gray-50 border border-gray-200 animate-pulse">
            <CardHeader>
              <div className="h-6 w-3/4 bg-gray-200 rounded"></div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Paragraph skeleton lines */}
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              </div>
              {/* Link skeleton */}
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </CardContent>
          </Card>
        ) : preview?.body ? (
          <Card className="bg-white border border-black rounded-lg shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
            <CardHeader className="mt-4 p-6">
              <div className="flex flex-col items-start">
                <CardTitle className=" bg-yellow-100 text-lg font-medium text-black tracking-tight">
                  Subject: {preview.subject}
                </CardTitle>
              </div>
            </CardHeader>

            <CardContent className="p-8 max-h-[80vh] overflow-y-auto">
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html: preview.body,
                }}
              />
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
};
export const ToneFeedbackForm = ({
  generateNewsletter,
  updateTone,
  initialFeedback = "", // Add initialFeedback prop with default value
}: {
  generateNewsletter: (tone: string, toneFeedback: string) => Promise<void>;
  updateTone: (tone: string, toneFeedback: string) => void;
  initialFeedback?: string; // Define initialFeedback prop
}) => {
  const [toneFeedback, setToneFeedback] = useState(initialFeedback); // Use initialFeedback as initial state
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (toneFeedback.trim()) {
      setIsLoading(true);
      try {
        await generateNewsletter("Make my own", toneFeedback);
        updateTone("Make my own", toneFeedback);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="flex flex-col justify-center gap-4 mx-auto">
      <Textarea
        className="w-full p-4 border border-black text-base bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
        placeholder="How would you like your newsletter to be written?"
        value={toneFeedback || initialFeedback}
        onChange={(e) => setToneFeedback(e.target.value)}
      />
      <Button className="w-full" onClick={handleSubmit} disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Generating...
          </>
        ) : (
          "Submit your voice"
        )}
      </Button>
    </div>
  );
};

export const SetFrequency = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const {
    config: { frequency, preferredDay },
    updateFrequency,
    preview,
  } = useContext(NewsletterContext);
  const { saveNewsletterConfig } = useNewsletterConfig();

  const frequencies = [
    { value: "daily", label: "Daily", available: true },
    { value: "weekly", label: "Weekly", available: true },
    { value: "biweekly", label: "Biweekly", available: false },
    { value: "monthly", label: "Monthly", available: false },
  ];

  const weekDays = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];

  const showDaySelector = frequency === "weekly" || frequency === "biweekly";

  const handleFrequencyChange = async ({
    frequency,
    preferredDay,
  }: {
    frequency: NewsletterConfig["frequency"];
    preferredDay?: NewsletterConfig["preferredDay"];
  }) => {
    updateFrequency({ frequency, preferredDay });
    await saveNewsletterConfig(preview, undefined, frequency, preferredDay);
  };

  return (
    <>
      <div
        className="mb-12 border border-black rounded-xl overflow-hidden shadow-md"
        style={{
          background:
            "linear-gradient(135deg, #e0f2fe 0%, #bfdbfe 50%, #dbeafe 100%)",
        }}
      >
        <div
          className="p-6 border-b border-black"
          style={{
            background: "linear-gradient(135deg, #bae6fd 0%, #93c5fd 100%)",
          }}
        >
          <h2 className="text-xl font-medium mb-2">
            How often would you like to receive your newsletter?
          </h2>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {frequencies.map((freq) => (
              <button
                key={freq.value}
                onClick={() =>
                  freq.available &&
                  handleFrequencyChange({
                    frequency: freq.value as NewsletterConfig["frequency"],
                  })
                }
                disabled={!freq.available}
                className={`p-4 border border-black rounded-lg transition-all ${
                  frequency === freq.value
                    ? "bg-black text-white rotate-2 transform"
                    : freq.available
                    ? "bg-white hover:bg-gray-50"
                    : "bg-gray-100 opacity-60 cursor-not-allowed"
                }`}
              >
                <div className="font-medium text-lg">{freq.label}</div>
                <div
                  className={
                    frequency === freq.value ? "text-white" : "text-gray-600"
                  }
                ></div>
                {freq.available && (
                  <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                    Available Now
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Day selector */}
          {showDaySelector && (
            <div className="mt-6">
              <div className="text-base font-medium mb-4 inline-block">
                Preferred Day for Delivery
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {weekDays.map((day) => (
                  <button
                    key={day.value}
                    onClick={() =>
                      handleFrequencyChange({
                        frequency,
                        preferredDay:
                          day.value as NewsletterConfig["preferredDay"],
                      })
                    }
                    className={`p-2 font-medium transition-all border border-black rounded-lg ${
                      preferredDay === day.value
                        ? "border-black bg-black text-white rotate-2 transform"
                        : "border-black bg-white hover:bg-gray-50"
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </>
  );
};
